/**
 * Common API Service utilities
 * Provides shared functionality for all API services
 */

import { safeConsole } from '../utils/productionUtils';

/**
 * Handle API errors consistently across all services
 * @param {Error} error - The error to handle
 * @param {string} context - Additional context about where the error occurred
 * @throws {Error} - Re-throws the error with enhanced message
 */
export const handleApiError = (error, context = '') => {
  const errorMessage = error.response?.data?.error || error.message || 'Unknown error occurred';
  const fullContext = context ? ` in ${context}` : '';
  
  safeConsole.error(`API Error${fullContext}`, errorMessage);
  
  // Re-throw with enhanced message
  throw new Error(errorMessage);
};

/**
 * Common API configuration
 */
export const API_CONFIG = {
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
};

/**
 * Make an authenticated API request
 * @param {string} url - The API endpoint URL
 * @param {object} options - Fetch options
 * @returns {Promise} - The API response
 */
export const makeApiRequest = async (url, options = {}) => {
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        ...API_CONFIG.headers,
        ...options.headers
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    handleApiError(error, `API request to ${url}`);
  }
};